import GridActions from '@/components/Base/GridActions/actions'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { TotalReturnContributionChart } from '@/components/ScenarioAnalysisReport/Charts/TotalReturnContributionChart'
import { ScenarioAnalysisReportNavigation } from '@/components/ScenarioAnalysisReport/ScenarioAnalysisReportNavigation'
import { ScenarioSecurityDetailsTable } from '@/components/ScenarioAnalysisReport/Tables/ScenarioSecurityDetailsTable'
import { useAppSelector } from '@/hooks/use-app-selector'
import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import {
  generateColumns,
  generateRows,
  getTop5AndBottom5Securities,
  transformToTotalContributionChartData,
} from '@/utils/scenarioAnalysisReportUtils/scenarioAnalysisReportUtils'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { CloseFullscreen, OpenInFull } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { getScenarioSecurityDetailReport } from '@services/axiomReport.service'
import React, { useEffect, useState } from 'react'

export const ScenarioAnalysisSecurityDetailsPage: React.FC = () => {
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const [loading, setLoading] = useState<boolean>(true)
  const [securityDetailsTableColumns, setSecurityDetailsTableColumns] =
    useState<ColDef[]>([])
  const [securityDetailsTableData, setSecurityDetailsTableData] = useState<
    any[]
  >([])

  const [totalContributionChartData, setTotalContributionChartData] =
    useState<any>([])
  const [gridReference, setGridReference] = useState<AgGridReact>()

  const fetchSecurityDetails = async () => {
    try {
      setLoading(true)

      const detailResponse = await getScenarioSecurityDetailReport(
        selectedReport.reportId,
      )
      const col = generateColumns(detailResponse)
      setSecurityDetailsTableColumns(col)
      const row = generateRows(detailResponse)
      setSecurityDetailsTableData(row)

      // scenarioByWeightChartData
      // const scenarioByWeightChartData =
      //   transformToScenarioByWeightChartData(detailResponse)
      // setSecurityDetailsChartData(scenarioByWeightChartData)

      // Total Contribution
      const totalContributionChartData =
        transformToTotalContributionChartData(detailResponse)
      const getTop10SecuritiesSeriesData = getTop5AndBottom5Securities(
        totalContributionChartData.series,
      )
      setTotalContributionChartData({
        ...totalContributionChartData,
        series: getTop10SecuritiesSeriesData,
      })
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSecurityDetails()
  }, [selectedReport])

  const [expandTotalContributionChart, setExpandTotalContributionChart] =
    useState(false)

  // const [expandSecuritiesByWeightChart, setExpandSecuritiesByWeightChart] =
  //   useState(false)

  return (
    <ReportDashboardLayout
      fitAllInView
      selectedReport={selectedReport}
      navigation={<ScenarioAnalysisReportNavigation />}
    >
      <Box
        display='grid'
        gridTemplateColumns='25rem 1fr'
        gridTemplateRows='1fr 1fr'
        gap={4}
        height='inherit'
      >
        <Box
          display='grid'
          gridRow={'1 / 3'}
          gridColumn={expandTotalContributionChart ? '1 / 3' : '1 / 2'}
          zIndex={expandTotalContributionChart ? 9 : 'initial'}
        >
          <Card>
            <CardHeader
              title={
                <Typography variant='h5'>
                  Total Contribution by Scenario / Security
                </Typography>
              }
              action={
                <Stack>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandTotalContributionChart(
                        !expandTotalContributionChart,
                      )
                    }
                  >
                    {expandTotalContributionChart ? (
                      <CloseFullscreen fontSize='inherit' />
                    ) : (
                      <OpenInFull fontSize='inherit' />
                    )}
                  </IconButton>
                </Stack>
              }
              sx={{ pb: 0 }}
            />
            <CardContent
              sx={{
                boxSizing: 'border-box',
                height: 'calc(100% - 4rem)',
              }}
            >
              {loading ? (
                <LoadingCard />
              ) : totalContributionChartData?.series?.length ? (
                <Box display='grid' alignItems='center' height='100%'>
                  <TotalReturnContributionChart
                    data={totalContributionChartData}
                    isFocused={expandTotalContributionChart}
                  />
                </Box>
              ) : (
                <NoDataToDisplayCard />
              )}
            </CardContent>
          </Card>
        </Box>
        {/* <Box
          display='grid'
          gridRow={expandSecuritiesByWeightChart ? '1 / 3' : '2 / 3'}
          gridColumn={expandSecuritiesByWeightChart ? '1 / 3' : '1 / 2'}
          zIndex={expandSecuritiesByWeightChart ? 9 : 'initial'}
        >
          <Card>
            <CardHeader
              title={<Typography variant='h5'>Securities by Weight</Typography>}
              action={
                <Stack>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandSecuritiesByWeightChart(
                        !expandSecuritiesByWeightChart,
                      )
                    }
                  >
                    {expandSecuritiesByWeightChart ? (
                      <CloseFullscreen fontSize='inherit' />
                    ) : (
                      <OpenInFull fontSize='inherit' />
                    )}
                  </IconButton>
                </Stack>
              }
              sx={{ pb: 0 }}
            />
            <CardContent
              sx={{
                boxSizing: 'border-box',
                height: 'calc(100% - 4rem)',
              }}
            >
              {loading ? (
                <LoadingCard />
              ) : securityDetailsChartData.length ? (
                <SecurityByWeightChart
                  data={securityDetailsChartData}
                  isFocused={expandSecuritiesByWeightChart}
                />
              ) : (
                <NoDataToDisplayCard />
              )}
            </CardContent>
          </Card>
        </Box> */}
        <Box display='grid' gridRow='1 / 3' gridColumn='2 / 3'>
          <Card>
            <CardHeader
              title={<Typography variant='h5'>Security Details</Typography>}
              sx={{ pb: 0 }}
              action={
                <Stack direction='row'>
                  <GridActions gridApiRef={gridReference} />
                </Stack>
              }
            />
            <CardContent sx={{ height: 'calc(100% - 60px)' }}>
              {loading ? (
                <LoadingCard />
              ) : securityDetailsTableData?.length ? (
                <Box display='grid' alignItems='stretch' height='100%'>
                  <ScenarioSecurityDetailsTable
                    rowData={securityDetailsTableData}
                    columnsList={securityDetailsTableColumns}
                    setGridReference={setGridReference}
                  />
                </Box>
              ) : (
                <NoDataToDisplayCard />
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </ReportDashboardLayout>
  )
}
